import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { AuthUser } from '@app/account/account-api.model';
import { AuthService } from '@app/shared/services/auth.service';
import { ProductName } from '@app/layout/v1/product-header.service';
import { NavLink } from '@app/layout/v1/product-header/product-header.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'dgx-channel-header',
  templateUrl: './channel-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChannelHeaderComponent implements OnInit {
  public navLinks: NavLink[];
  public useRouterLinks: boolean;
  public readonly PRODUCT_NAME = ProductName.ExtendedEnterprise;

  private readonly CHANNEL_PREFIX = 'channel';
  private i18n = this.translateService.instant([
    'Channel_OrganizationsNav',
    'Channel_GroupsNav',
    'Channel_CatalogNav',
    'Channel_DefaultsNav',
    'Channel_InsightsNav',
    'Channel_ReturnToOrgNav',
    'OrganizationsController_Reporting',
  ]);
  private authUser: AuthUser;

  constructor(
    private translateService: TranslateService,
    private authService: AuthService
  ) {}

  private get canViewGroups() {
    return (
      this.authUser.defaultOrgInfo.permissions.manageGroups ||
      this.authUser.defaultOrgInfo.permissions.createOpenGroups ||
      this.authUser.defaultOrgInfo.permissions.createPrivateGroups
    );
  }
  private get canViewSettings() {
    return (
      this.authUser.defaultOrgInfo.permissions.editSettings ||
      this.authUser.defaultOrgInfo.permissions.editPermissions
    );
  }

  private get canViewCatalog() {
    return (
      this.authUser.defaultOrgInfo.permissions.manageContent ||
      this.authUser.defaultOrgInfo.permissions.managePathways ||
      this.authUser.defaultOrgInfo.permissions.manageTargets ||
      this.authUser.defaultOrgInfo.permissions.shareBundledContent
    );
  }

  private get canViewReporting() {
    return this.authUser.defaultOrgInfo.permissions.manageContent;
  }

  public ngOnInit(): void {
    this.useRouterLinks = window.location.pathname.startsWith(
      '/' + this.CHANNEL_PREFIX
    );
    this.authUser = this.authService.authUser;
    this.initLinks();
  }

  private initLinks() {
    this.navLinks = [
      {
        title: this.i18n.Channel_OrganizationsNav,
        isVisible: () => true,
        isActive: () => window.location.pathname.indexOf('/tenants') > -1,
        href: `/${this.CHANNEL_PREFIX}/tenants`,
        routerLink: [`/${this.CHANNEL_PREFIX}/tenants`],
        dgat: '_ChannelHeader-3a0',
      },
      {
        title: this.i18n.Channel_CatalogNav,
        isVisible: () => this.canViewCatalog,
        isActive: () => window.location.pathname.indexOf('/catalog') > -1,
        href: `/${this.CHANNEL_PREFIX}/catalog`,
        routerLink: [`/${this.CHANNEL_PREFIX}/catalog`],
        dgat: '_ChannelHeader-e09',
      },
      {
        title: this.i18n.Channel_GroupsNav,
        isVisible: () => this.canViewGroups,
        isActive: () => window.location.pathname.indexOf('/groups') > -1,
        href: `/${this.CHANNEL_PREFIX}/groups`,
        routerLink: [`/${this.CHANNEL_PREFIX}/groups`],
        dgat: '_ChannelHeader-c94',
      },
      {
        title: this.i18n.Channel_InsightsNav,
        isVisible: () => this.authUser.defaultOrgInfo.permissions.viewReports,
        isActive: () =>
          window.location.pathname.indexOf('/channelInsights') > -1,
        href: `/${this.CHANNEL_PREFIX}/channelInsights`,
        routerLink: [`/${this.CHANNEL_PREFIX}/channelInsights`],
        dgat: 'channelHeader-bac',
      },
      {
        title: this.i18n.Channel_DefaultsNav,
        isVisible: () => this.canViewSettings,
        isActive: () => window.location.pathname.indexOf('/settings') > -1,
        href: `/${this.CHANNEL_PREFIX}/settings`,
        routerLink: [`/${this.CHANNEL_PREFIX}/settings`],
        dgat: '_ChannelHeader-a12',
      },
      {
        title: this.i18n.OrganizationsController_Reporting,
        isVisible: () => this.canViewReporting,
        isActive: () => window.location.pathname.indexOf('/reporting') > -1,
        href: `/${this.CHANNEL_PREFIX}/reporting`,
        routerLink: [`/${this.CHANNEL_PREFIX}/reporting`],
        dgat: '_ChannelHeader-2y3',
      },
    ];
  }
}
